import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Button } from '../catalyst/button'
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../catalyst/description-list'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { formatDate } from '../lib/farm-log-layout'
import { useFarms } from '../lib/farms'
import { inventoryPath, STOCK_CATEGORY_LABEL, STOCK_ENTRY_LABEL, stockPath, useStockItem } from '../lib/inventory'
import { enteredText, formatAmount, formatIn, packageBreakdown, STOCK_DIMENSION_LABEL, unitLong, unitShort } from '../lib/stock-units'
import type { StockItem, StockMovement } from '../lib/types'
import { Shell } from './shell'

// One stock item: how much is on hand, and every entry that got it there. Entries are recorded on a
// page of their own (stock-entry-page) and cannot be edited: a count puts a mistake right.

const KIND_LABEL: Record<StockMovement['kind'], string> = { RECEIVED: 'Received', USED: 'Used', ADJUSTED: 'Count' }

const signed = (item: StockItem, quantity: number) => `${quantity > 0 ? '+' : '−'}${formatAmount(item, Math.abs(quantity))}`

function Ledger({ item, movements }: { item: StockItem; movements: StockMovement[] }) {
  if (movements.length === 0) return <Text className="mt-2">Nothing recorded yet. Record a delivery or a count to set what is on hand.</Text>
  return (
    <ul className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
      {movements.map((m) => {
        // What was typed, when it says more than the change does: a count, packages, or another unit.
        const typed = m.entered ? enteredText(item.dimension, item.package?.name ?? null, m.entered) : ''
        const showTyped = typed && (m.kind === 'ADJUSTED' || m.entered?.packages !== null || m.entered?.unit !== item.shownIn)
        const detail = [showTyped ? (m.kind === 'ADJUSTED' ? `Counted ${typed}` : `Entered as ${typed}`) : null, m.note].filter(Boolean).join(' · ')
        return (
          <li key={m.id} className="flex items-baseline justify-between gap-4 py-3">
            <span className="min-w-0">
              <span className="block text-base/6 font-medium text-zinc-950 sm:text-sm/6 dark:text-white">
                {KIND_LABEL[m.kind]} <span className="font-normal text-zinc-500 dark:text-zinc-400">· {formatDate(m.occurredOn)}</span>
              </span>
              {detail && <span className="block text-sm/6 text-zinc-500 dark:text-zinc-400">{detail}</span>}
            </span>
            <span className={`shrink-0 text-base/6 tabular-nums sm:text-sm/6 ${m.quantity < 0 ? 'text-zinc-500 dark:text-zinc-400' : 'text-zinc-950 dark:text-white'}`}>
              {signed(item, m.quantity)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export function StockItemPage() {
  const { farmId, stockItemId } = useParams<{ farmId: string; stockItemId: string }>()
  const navigate = useNavigate()
  const { org } = useAuth()
  const { getFarm, loading: farmsLoading } = useFarms()
  const { item, movements, loading, error, archiveItem } = useStockItem(org?.id, farmId, stockItemId)
  const [confirming, setConfirming] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const canWrite = org?.role !== 'AGRONOMIST'

  const farm = farmId ? getFarm(farmId) : undefined
  if (!farmsLoading && !farm) return <Navigate to="/" replace />
  if (!farm || !org || !farmId) return null

  const back = inventoryPath(farmId, 'STOCK')

  async function handleArchive() {
    setArchiving(true)
    setArchiveError(null)
    try {
      await archiveItem()
      navigate(back, { replace: true })
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Could not archive it.')
      setArchiving(false)
    }
  }

  return (
    <Shell>
      <Link href={back} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        Stock
      </Link>

      {loading ? (
        <Text className="mt-6">Loading…</Text>
      ) : !item ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>{error ?? 'Not found.'}</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <Heading className="flex flex-wrap items-center gap-2">
                {item.name}
                {item.archivedAt && <Badge color="amber">Archived</Badge>}
              </Heading>
              <Text>{STOCK_CATEGORY_LABEL[item.category]}</Text>
            </div>
            {canWrite && !item.archivedAt && (
              <div className="flex gap-3">
                <Button outline onClick={() => setConfirming(true)} disabled={confirming}>
                  Archive
                </Button>
                <Button outline href={`${stockPath(farmId, item.id)}/edit`}>
                  Edit
                </Button>
              </div>
            )}
          </div>

          {confirming && (
            <div className="mt-6 rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-950/10 dark:bg-white/5 dark:ring-white/10">
              <Text>Archive {item.name}? It is hidden from the stock list and takes no new entries. Its entries stay.</Text>
              {archiveError && <Text className="mt-2 text-red-600 dark:text-red-400">{archiveError}</Text>}
              <div className="mt-4 flex gap-3">
                <Button color="red" onClick={handleArchive} disabled={archiving}>
                  {archiving ? 'Archiving…' : 'Archive'}
                </Button>
                <Button plain onClick={() => setConfirming(false)} disabled={archiving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="mt-8 rounded-lg bg-zinc-50 p-5 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:ring-white/10">
            <Text>On hand</Text>
            <p className={`mt-1 text-3xl/9 font-semibold tabular-nums ${item.onHand < 0 ? 'text-red-600 dark:text-red-400' : 'text-zinc-950 dark:text-white'}`}>
              {formatAmount(item, item.onHand)}
            </p>
            {packageBreakdown(item) && <Text>{packageBreakdown(item)}</Text>}
            {item.onHand < 0 && <Text className="mt-1">More use than stock has been recorded. Record the delivery, or a count.</Text>}
            {canWrite && !item.archivedAt && (
              <div className="mt-4 grid grid-cols-3 gap-2 sm:flex sm:gap-3">
                {(['RECEIVED', 'USED', 'COUNTED'] as const).map((kind) => (
                  <Button key={kind} outline href={`${stockPath(farmId, item.id)}/record?kind=${kind}`}>
                    {STOCK_ENTRY_LABEL[kind].title}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <DescriptionList className="mt-8">
            <DescriptionTerm>Measured as</DescriptionTerm>
            <DescriptionDetails>{STOCK_DIMENSION_LABEL[item.dimension].label}</DescriptionDetails>
            {item.dimension !== 'COUNT' && (
              <>
                <DescriptionTerm>Shown in</DescriptionTerm>
                <DescriptionDetails>
                  {unitLong(item.dimension, item.shownIn)}
                  {item.displayUnit === null && <span className="text-zinc-500 dark:text-zinc-400"> · farm setting</span>}
                </DescriptionDetails>
              </>
            )}
            <DescriptionTerm>Comes in</DescriptionTerm>
            <DescriptionDetails>
              {item.package ? `${item.package.name} of ${formatIn(item.dimension, item.package.size, item.package.unit, 4)}${unitShort(item.dimension, item.package.unit) ? '' : ' each'}` : '—'}
            </DescriptionDetails>
            {item.category === 'PESTICIDE' && (
              <>
                <DescriptionTerm>Registered product</DescriptionTerm>
                <DescriptionDetails>
                  {item.product ? (
                    <Link href={`/pesticides/${item.pesticideProductId}`}>
                      {item.product.name} · PCP {item.product.pcpNumber}
                    </Link>
                  ) : (
                    'Not linked'
                  )}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>

          <Subheading className="mt-10">Entries</Subheading>
          <Ledger item={item} movements={movements ?? []} />
        </>
      )}
    </Shell>
  )
}
